import { inject, Injectable } from '@angular/core';
import { ColumnConfig, SessionCookieService, TableService } from '@docpath/linces-angular-components/common';
import { DpHeaderAction, DpHeaderOption, DpHeaderOptionGroup, DpMenuPlacement, HeaderAction, SearchMode, SearchOptionType, SearchRowInfo, SearchRowInfoNumber, SearchRowInfoSelect } from '@docpath/webcomponents';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { Observable } from 'rxjs';
import { Constants } from 'src/app/constants/constants';
import { CloudService } from 'src/app/shared/services/cloud.service';

import { RoutingPath } from '../../../../constants/routing-path';
import { Job, JobConfiguration, JobPreview, JobStatus } from '../../models/job';
import { StatisticsFormService } from '../../services/statistics-form.service';
import { StatisticsHttpService } from '../../services/statistics-http.service';

@Injectable({
    providedIn: 'root'
})
export class StatisticsHeaderService {
    private readonly i18nPrefix = 'statistics';

    private readonly sessionCookieService = inject(SessionCookieService);
    private readonly tableService = inject(TableService);
    private readonly statisticsHttpService = inject(StatisticsHttpService);
    private readonly statisticsFormService = inject(StatisticsFormService);
    private readonly cloudService = inject(CloudService);

    getRightActions(): HeaderAction[] {
        const columnsConfig = this.tableService.getColumns(this.i18nPrefix);

        return [
            this.initSearchAction(),
            this.initShowColumnsAction(columnsConfig)
        ];
    }

    getMoreActions(selectedRow: Job[]): HeaderAction[] {
        const actions = [];
        const jobSelected = selectedRow.length === 1 ? selectedRow[0] : null;

        if (jobSelected) {
            // Only show ViewRelaunched if the mode is not a cloud mode (admin or normal user in usual mode)
            if (!this.cloudService.isModeCloud()) {
                this.hasRelaunchedJobs(jobSelected.idJob).subscribe((hasRelaunchedJobs: boolean) => {
                    if (hasRelaunchedJobs) {
                        actions.push(this.initViewRelaunchedJobsAction(jobSelected));
                    }
                });
            }

            // Only show actions if the user is not a normal user in cloud mode (admin or normal user in usual mode or admin user in cloud mode)
            if (jobSelected.log && !(this.cloudService.isModeCloud() && this.sessionCookieService.getUserLogged()?.role === Constants.ROLE_NORMAL)) {
                actions.push(this.initLogAction(jobSelected));
            }
        }

        return actions;
    }

    getLeftActions(selectedRow: Job[]): HeaderAction[] {
        const actions = [];
        const jobSelected = selectedRow.length === 1 ? selectedRow[0] : null;

        if (jobSelected) {
            // Only show actions if the mode is not a cloud mode (admin or normal user in usual mode)
            if (jobSelected.relaunch && !this.cloudService.isModeCloud()) {
                actions.push(this.initRelaunchJobAction(jobSelected));
            }
        }

        return actions;
    }

    getSearchAction(actions: HeaderAction[]): HeaderAction {
        return actions[0];
    }


    initSearchAvailableOptions(): SearchRowInfo[] {
        const columnsConfig = this.tableService.getColumns(this.i18nPrefix);

        const filters = [];

        filters.push(new SearchRowInfoNumber({ checked: false, key: Constants.STATISTICS_JOB_IDJOB, value: undefined, mode: SearchMode.exactly, placeholder: true, min: 0 }));
        if (!(this.cloudService.isModeCloud() && this.sessionCookieService.getUserLogged()?.role === Constants.ROLE_NORMAL)) {
            filters.push(new SearchRowInfo(false, Constants.STATISTICS_JOB_DGE, undefined, SearchOptionType.TEXT, SearchMode.exactly, true, true));
        }

        filters.push(new SearchRowInfoNumber({ checked: false, key: this.cloudService.isModeCloud() ? Constants.STATISTICS_JOB_TRANSACTIONS : Constants.STATISTICS_JOB_DOCUMENTS, value: undefined, mode: SearchMode.exactly, placeholder: true, min: 0 }));
        filters.push(new SearchRowInfoNumber({ checked: false, key: Constants.STATISTICS_JOB_PAGES, value: undefined, mode: SearchMode.exactly, placeholder: true, min: 0 }));
        filters.push(new SearchRowInfo(false, Constants.STATISTICS_JOB_FORM_NAME, undefined, SearchOptionType.TEXT, SearchMode.exactly, true, true));
        filters.push(new SearchRowInfoNumber({ checked: false, key: Constants.STATISTICS_JOB_QUEUE_TIME, value: undefined, mode: SearchMode.exactly, placeholder: true, min: 0 }));
        filters.push(new SearchRowInfoNumber({ checked: false, key: Constants.STATISTICS_JOB_EXECUTION_TIME, value: undefined, mode: SearchMode.exactly, placeholder: true, min: 0 }));
        filters.push(new SearchRowInfo(false, Constants.STATISTICS_JOB_TAG1, undefined, SearchOptionType.TEXT, SearchMode.exactly, true, true));
        filters.push(new SearchRowInfo(false, Constants.STATISTICS_JOB_TAG2, undefined, SearchOptionType.TEXT, SearchMode.exactly, true, true));
        filters.push(new SearchRowInfo(false, Constants.STATISTICS_JOB_TAG3, undefined, SearchOptionType.TEXT, SearchMode.exactly, true, true));
        filters.push(new SearchRowInfo(false, Constants.STATISTICS_JOB_TAG4, undefined, SearchOptionType.TEXT, SearchMode.exactly, true, true));
        filters.push(new SearchRowInfo(false, Constants.STATISTICS_JOB_TAG5, undefined, SearchOptionType.TEXT, SearchMode.exactly, true, true));
        filters.push(new SearchRowInfoSelect(true, Constants.STATISTICS_JOB_PREVIEW, JobPreview.NO, SearchMode.is, this.getPreviewFilterOptions(), true, true));
        filters.push(new SearchRowInfoSelect(false, Constants.STATISTICS_JOB_STATUS, undefined, SearchMode.is, this.getStatusFilterOptions(), true, true));

        filters.forEach(filter => {
            const columnConfig = columnsConfig.find(column => column.name === filter.key);

            if (columnConfig) {
                filter.visible = columnConfig.visible;
            }
        });

        return filters;
    }

    hasRelaunchedJobs(idJob: number): Observable<boolean> {
        return this.statisticsHttpService.hasRelaunchedJobs(idJob);
    }

    private getStatusFilterOptions(): Map<string, string> {
        const i18nPrefix = 'statistics';

        return new Map<string, string>([
            [`${i18nPrefix}.table.status.option.OK`, JobStatus.OK],
            [`${i18nPrefix}.table.status.option.KO`, JobStatus.KO],
            [`${i18nPrefix}.table.status.option.warning`, JobStatus.warning]
        ]);
    }

    private getPreviewFilterOptions(): Map<string, string> {
        const i18nPrefix = 'statistics';

        return new Map<string, string>([
            [`${i18nPrefix}.table.preview.option.YES`, JobPreview.YES],
            [`${i18nPrefix}.table.preview.option.NO`, JobPreview.NO]
        ]);
    }

    private initSearchAction(): HeaderAction {
        const action = new HeaderAction(`${this.i18nPrefix}.header.search.label`, { cssClass: 'btn btn-dpsecondary' });
        action.tooltip = `${this.i18nPrefix}.header.search.tooltip`;
        action.icon = faMagnifyingGlass;

        return action;
    }

    private initLogAction(job: Job): HeaderAction {
        const action = new HeaderAction(`${this.i18nPrefix}.header.log.label`);

        action.tooltip = `${this.i18nPrefix}.header.log.tooltip`;
        action.action = () => this.logJob(job);

        return action;
    }

    private logJob(job: Job): void {
        if (job.idJob && job.idDGE) {
            const context = window.location.pathname.split('/')[1];
            let path = RoutingPath.PATH_STATISTICS_LOGS.replace(':idJob', `${job.idJob}`);
            path = path.replace(':dgeId', `${job.idDGE}`);
            const url = `/${context}/${path}`;

            window.open(`${url}`, '_blank');
        }
    }

    private initRelaunchJobAction(job: Job): HeaderAction {
        const action = new HeaderAction(`${this.i18nPrefix}.header.relaunch-job.label`);

        action.tooltip = `${this.i18nPrefix}.header.relaunch-job.tooltip`;
        action.action = () => {
            this.getJobConfiguration(job.idJob).subscribe(
                (jobConfiguration: JobConfiguration) => {
                    this.statisticsFormService.openRelaunchJobForm(jobConfiguration);
                });
        };

        return action;
    }

    private getJobConfiguration(idJob: number): Observable<JobConfiguration> {
        return this.statisticsHttpService.getJobConfiguration(idJob);
    }

    private initViewRelaunchedJobsAction(job: Job): HeaderAction {
        const action = new HeaderAction(`${this.i18nPrefix}.header.view-relaunched-jobs.label`);

        action.tooltip = `${this.i18nPrefix}.header.view-relaunched-jobs.tooltip`;
        action.action = () => {
            this.getRelaunchedJobs(job.idJob).subscribe(
                (relaunchedJobs: Job[]) => {
                    this.statisticsFormService.openViewRelaunchedJobsForm(job.idJob, relaunchedJobs);
                });
        };

        return action;
    }

    private getRelaunchedJobs(idJob: number): Observable<Job[]> {
        return this.statisticsHttpService.getRelaunchedJobs(idJob);
    }

    private initShowColumnsAction(columnsConfig: ColumnConfig[]): HeaderAction {
        const optionGroups: { value: string; label: string; }[][] = [[]];

        optionGroups[0].push({ value: Constants.STATISTICS_JOB_IDJOB, label: 'id' });
        if (!(this.cloudService.isModeCloud() && this.sessionCookieService.getUserLogged()?.role === Constants.ROLE_NORMAL)) {
            optionGroups[0].push({ value: Constants.STATISTICS_JOB_DGE, label: 'dge' });
        }

        optionGroups[0].push({ value: this.cloudService.isModeCloud() ? Constants.STATISTICS_JOB_TRANSACTIONS : Constants.STATISTICS_JOB_DOCUMENTS, label: this.cloudService.isModeCloud() ? 'transactions' : 'documents' });
        optionGroups[0].push({ value: Constants.STATISTICS_JOB_PAGES, label: 'pages' });
        optionGroups[0].push({ value: Constants.STATISTICS_JOB_FORM_NAME, label: 'form-name' });
        optionGroups[0].push({ value: Constants.STATISTICS_JOB_STARTED, label: 'started' });
        optionGroups[0].push({ value: Constants.STATISTICS_JOB_FINISHED, label: 'finished' });
        optionGroups[0].push({ value: Constants.STATISTICS_JOB_QUEUE_TIME, label: 'queue-time' });
        optionGroups[0].push({ value: Constants.STATISTICS_JOB_EXECUTION_TIME, label: 'execution-time' });
        optionGroups[0].push({ value: Constants.STATISTICS_JOB_TAG1, label: 'tag1' });
        optionGroups[0].push({ value: Constants.STATISTICS_JOB_TAG2, label: 'tag2' });
        optionGroups[0].push({ value: Constants.STATISTICS_JOB_TAG3, label: 'tag3' });
        optionGroups[0].push({ value: Constants.STATISTICS_JOB_TAG4, label: 'tag4' });
        optionGroups[0].push({ value: Constants.STATISTICS_JOB_TAG5, label: 'tag5' });
        optionGroups[0].push({ value: Constants.STATISTICS_JOB_PREVIEW, label: 'preview.label' });
        optionGroups[0].push({ value: Constants.STATISTICS_JOB_STATUS, label: 'status.label' });

        const disabledColumns: string[] = [];

        disabledColumns.push(Constants.STATISTICS_JOB_IDJOB);
        if (!(this.cloudService.isModeCloud() && this.sessionCookieService.getUserLogged()?.role === Constants.ROLE_NORMAL)) {
            disabledColumns.push(Constants.STATISTICS_JOB_DGE);
        }

        disabledColumns.push(this.cloudService.isModeCloud() ? Constants.STATISTICS_JOB_TRANSACTIONS : Constants.STATISTICS_JOB_DOCUMENTS);
        disabledColumns.push(Constants.STATISTICS_JOB_PAGES);
        disabledColumns.push(Constants.STATISTICS_JOB_FORM_NAME);
        disabledColumns.push(Constants.STATISTICS_JOB_STARTED);
        disabledColumns.push(Constants.STATISTICS_JOB_PREVIEW);
        disabledColumns.push(Constants.STATISTICS_JOB_STATUS);

        return new DpHeaderAction({
            title: `${this.i18nPrefix}.header.show-columns.label`,
            customStyles: { cssClass: 'btn btn-dpsecondary' },
            tooltip: `${this.i18nPrefix}.header.show-columns.tooltip`,
            menuPlacement: DpMenuPlacement.Left,
            checklist: optionGroups.map(group =>
                new DpHeaderOptionGroup({
                    options: group.map(column =>
                        new DpHeaderOption({
                            label: `${this.i18nPrefix}.table.${column.label ?? column.value}`,
                            value: column.value,
                            tooltip: `${this.i18nPrefix}.table.${column.label ?? column.value}`,
                            checked: columnsConfig.find(columnConfig => columnConfig.name === column.value)?.visible,
                            disabled: disabledColumns.includes(column.value)
                        })
                    )
                })
            )
        });
    }

}
